import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"
import $ from "jquery"
import '@fonticonpicker/fonticonpicker'

// Connects to data-controller="settings--device-types-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "printingNeedsSelect",
        "iconInput",
        "editModal",
        "editForm",
        "editNameInput",
        "editPrintingNeedsSelect",
        "editIconInput",
        "editIdInput"
    ]

    connect() {
        this.initializeTable()
        this.loadDeviceTypes()
        this.initializeIconPickers()
        this._setupRowDoubleClick()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _setupRowDoubleClick() {
        if (!this.hasTableTarget) return
        this.tableTarget.addEventListener('dblclick', (e) => {
            const tr = e.target.closest('tbody tr')
            if (!tr) return
            const argusTable = this.application.getControllerForElementAndIdentifier(this.tableTarget, 'argus--table')
            if (!argusTable) return
            const rows = Array.from(this.tableTarget.querySelectorAll('tbody tr'))
            const index = rows.indexOf(tr)
            const data = argusTable.getData()
            const row = data[index]
            if (row && row.id) {
                window.location.href = `/settings/device-types/${row.id}`
            }
        })
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadDeviceTypes() {
        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch('/api/device_types/device_type/list', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch device types')
            }

            const data = await response.json()
            this.populateTable(data.device_types || [])
        } catch (error) {
            console.error('Error loading device types:', error)
            sendFailureNotification('Failed to load device types')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(deviceTypes) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(deviceTypes)
            }
        }
    }

    openCreateModal(event) {
        event.preventDefault()
        this.showCreateModal()
    }

    showCreateModal() {
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        if (this.hasIconInputTarget) {
            this.iconInputTarget.value = ''
        }
        if (this.hasPrintingNeedsSelectTarget) {
            this.printingNeedsSelectTarget.value = 'none'
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/device_types/device_type/create', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to create device type')
            }

            // Close modal and wait for it to fully hide before proceeding
            await new Promise(resolve => {
                const modal = bootstrap.Modal.getInstance(this.createModalTarget)
                if (modal) {
                    this.createModalTarget.addEventListener('hidden.bs.modal', () => {
                        modal.dispose()
                        resolve()
                    }, { once: true })
                    modal.hide()
                } else {
                    resolve()
                }
            })

            // Reload table
            await this.loadDeviceTypes()

            sendSuccessNotification('Device type created successfully')
        } catch (error) {
            console.error('Error creating device type:', error)
            sendFailureNotification('Failed to create device type')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const deviceTypeId = button.dataset.deviceTypeId

        // Fetch device type details
        this.fetchDeviceTypeDetails(deviceTypeId)
    }

    async fetchDeviceTypeDetails(deviceTypeId) {
        try {
            const response = await fetch(`/api/device_types/device_type/describe?id=${deviceTypeId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch device type details')
            }

            const data = await response.json()
            const deviceType = data.device_types[0]

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = deviceType.name || ''
            }
            
            if (this.hasEditPrintingNeedsSelectTarget) {
                let printingNeeds = 'none'
                if (deviceType.printer) {
                    printingNeeds = 'printer'
                } else if (deviceType.sends_printouts) {
                    printingNeeds = 'sends_printouts'
                }
                this.editPrintingNeedsSelectTarget.value = printingNeeds
            }
            
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = deviceType.id
            }

            // Set icon value - ensure icon picker is initialized first
            if (this.hasEditIconInputTarget && deviceType.icon) {
                // Wait a bit to ensure icon picker is fully initialized
                setTimeout(() => {
                    if (typeof $.fn.fontIconPicker === 'function') {
                        const picker = $(this.editIconInputTarget).data('fontIconPicker')
                        if (picker) {
                            picker.setIcon(deviceType.icon)
                        } else {
                            // Fallback to setting the input value directly
                            this.editIconInputTarget.value = deviceType.icon
                        }
                    } else {
                        this.editIconInputTarget.value = deviceType.icon
                    }
                }, 100)
            }

            // Show modal
            const modal = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
            modal.show()
        } catch (error) {
            console.error('Error fetching device type details:', error)
            sendFailureNotification('Failed to load device type details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/device_types/device_type/update', {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to update device type')
            }

            // Close modal and wait for it to fully hide before proceeding
            await new Promise(resolve => {
                const modal = bootstrap.Modal.getInstance(this.editModalTarget)
                if (modal) {
                    this.editModalTarget.addEventListener('hidden.bs.modal', () => {
                        modal.dispose()
                        resolve()
                    }, { once: true })
                    modal.hide()
                } else {
                    resolve()
                }
            })

            // Reload table
            await this.loadDeviceTypes()

            sendSuccessNotification('Device type updated successfully')
        } catch (error) {
            console.error('Error updating device type:', error)
            sendFailureNotification('Failed to update device type')
        }
    }

    async deleteDeviceType(event) {
        event.preventDefault()

        const button = event.currentTarget
        const deviceTypeId = button.dataset.deviceTypeId

        // Get device type name from table row
        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const deviceTypeName = nameCell ? nameCell.textContent : 'this device type'

        if (!confirm(`Are you sure you want to delete ${deviceTypeName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', deviceTypeId)

        try {
            const response = await fetch('/api/device_types/device_type/delete', {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to delete device type')
            }

            // Reload table
            await this.loadDeviceTypes()

            sendSuccessNotification('Device type deleted successfully')
        } catch (error) {
            console.error('Error deleting device type:', error)
            sendFailureNotification('Failed to delete device type')
        }
    }

    viewManufacturers(event) {
        event.preventDefault()

        const button = event.currentTarget
        const deviceTypeId = button.dataset.deviceTypeId

        // Navigate to manufacturers page
        window.location.href = `/settings/device-types/${deviceTypeId}`
    }

    initializeIconPickers() {
        // Initialize icon pickers for both create and edit modals
        this.loadIconOptions()
    }

    async loadIconOptions() {
        try {
            const response = await fetch('/settings/icon_selection', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            })

            if (!response.ok) {
                console.error('Failed to load icon options')
                return
            }

            const data = await response.json()
            const classPrefix = data.preferences.fontPref.prefix
            const icons = []
            const searchTerms = []

            data.icons.forEach(icon => {
                icons.push(classPrefix + icon.properties.name)
                if (icon.icon && icon.icon.tags && icon.icon.tags.length) {
                    searchTerms.push(icon.properties.name + ' ' + icon.icon.tags.join(' '))
                } else {
                    searchTerms.push(icon.properties.name)
                }
            })

            // Initialize icon pickers with loaded icons
            if (this.hasIconInputTarget && typeof $.fn.fontIconPicker === 'function') {
                $(this.iconInputTarget).fontIconPicker({
                    theme: 'fip-bootstrap',
                    emptyIcon: true
                })
                $(this.iconInputTarget).fontIconPicker().setIcons(icons, searchTerms)
            }

            if (this.hasEditIconInputTarget && typeof $.fn.fontIconPicker === 'function') {
                $(this.editIconInputTarget).fontIconPicker({
                    theme: 'fip-bootstrap',
                    emptyIcon: true
                })
                $(this.editIconInputTarget).fontIconPicker().setIcons(icons, searchTerms)
            }
        } catch (error) {
            console.error('Error loading icon options:', error)
        }
    }
}
